import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  inboundPaymentsApi,
  type CounterpartyScreening,
  type InboundPaymentDetail,
  type PayerEdit,
} from '../api/inbound-payments.api.js';
import { APP_LOCALE } from '../utils/locale.js';

/**
 * Who sent this money, and whether they are sanctioned (DEV-96).
 *
 * The reviewer can put right what the partner told us about the sender --
 * person or company, country, address, registration number, bank -- and
 * screen the sender, and a company sender's UBO, at the provider. All of it is
 * optional for the review: nothing here blocks a decision.
 *
 * The provider screens profiles, not names, and keeps every profile it is
 * given. One profile per identity: a company by its registration number,
 * otherwise by name and country. Screening the same sender again reuses it.
 */

const UNDECIDED = new Set(['PENDING_REVIEW', 'UNATTRIBUTED']);

const STATE_CHIP: Readonly<Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }>> = {
  IN_PROGRESS: { label: 'Screening…', color: 'info' },
  CLEAR: { label: 'Clear — no matches', color: 'success' },
  MATCHES_FOUND: { label: 'Possible matches', color: 'warning' },
  FAILED: { label: 'Could not be screened', color: 'error' },
};

export function SenderScreeningPanel({
  payment,
  canAct,
}: {
  readonly payment: InboundPaymentDetail;
  readonly canAct: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [uboFor, setUboFor] = useState<CounterpartyScreening | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const screenings = useQuery({
    queryKey: ['counterparty-screenings', payment.id],
    queryFn: () => inboundPaymentsApi.counterpartyScreenings(payment.id),
    // The provider answers asynchronously; look again while a run is open.
    refetchInterval: (query) => ((query.state.data ?? []).some((s) => s.state === 'IN_PROGRESS') ? 15_000 : false),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['counterparty-screenings', payment.id] });
  };
  const failed = (err: unknown): void => setFailure(err instanceof Error ? err.message : 'That could not be done.');

  const screenSender = useMutation({
    mutationFn: () => inboundPaymentsApi.screenCounterparty(payment.id, {
      role: 'PAYER',
      entityType: payment.payerType === 'PERSON' ? 'PERSON' : 'COMPANY',
      name: payment.payerName ?? '',
      country: payment.payerCountry ?? '',
      ...(payment.payerType === 'COMPANY' && payment.payerRegistrationNumber ? { registrationNumber: payment.payerRegistrationNumber } : {}),
      ...(payment.payerAddress ? { address: payment.payerAddress } : {}),
    }),
    onSuccess: () => { setFailure(null); refresh(); },
    onError: failed,
  });

  const undecided = UNDECIDED.has(payment.status);
  const missing = [
    payment.payerName ? null : 'the sender name',
    payment.payerCountry ? null : 'the sender country',
    payment.payerType ? null : 'whether the sender is a person or a company',
  ].filter((m): m is string => m !== null);
  const rows = screenings.data ?? [];

  return (
    <Box component="section" aria-label="Sender screening" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle2">Sender screening</Typography>
        {canAct ? (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {undecided ? <Button size="small" variant="outlined" onClick={() => setEditing(true)}>Edit sender details</Button> : null}
            <Button
              size="small"
              variant="contained"
              disabled={missing.length > 0 || screenSender.isPending}
              onClick={() => screenSender.mutate()}
            >
              {screenSender.isPending ? 'Starting…' : 'Screen sender'}
            </Button>
          </Stack>
        ) : null}
      </Stack>

      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
        <Field label="Sender is" value={payment.payerType === 'COMPANY' ? 'A company' : payment.payerType === 'PERSON' ? 'A person' : 'Not established'} />
        {payment.payerType === 'COMPANY' ? <Field label="Registration number" value={payment.payerRegistrationNumber ?? 'Not recorded — name and country identify it'} /> : null}
        <Field label="Address (reviewer)" value={payment.payerAddress ?? 'Not recorded'} />
        {payment.payerEditedAt ? <Field label="Details last corrected" value={new Date(payment.payerEditedAt).toLocaleString(APP_LOCALE)} /> : null}
      </Box>

      {canAct && missing.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          To screen the sender, record {missing.join(', ')} first{undecided ? ' (Edit sender details)' : ''}.
        </Typography>
      ) : null}
      {failure !== null ? <Alert severity="warning" onClose={() => setFailure(null)}>{failure}</Alert> : null}
      {screenings.isError ? <Alert severity="warning">The screenings could not be read. They have not been lost — try again.</Alert> : null}

      {rows.length > 0 ? (
        <Stack spacing={1} aria-label="Screenings of this sender">
          {rows.map((s) => (
            <Box key={s.id} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Chip size="small" variant="outlined" label={s.role === 'UBO' ? 'UBO' : 'Sender'} />
                <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.entityType === 'COMPANY' ? 'Company' : 'Person'} · {s.country}{s.registrationNumber ? ` · reg. ${s.registrationNumber}` : ''}
                </Typography>
                <Chip
                  size="small"
                  color={(STATE_CHIP[s.state] ?? { color: 'default' }).color}
                  label={`${(STATE_CHIP[s.state] ?? { label: s.state }).label}${s.state === 'MATCHES_FOUND' ? ` (${String(s.matchCount)})` : ''}`}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Requested {new Date(s.requestedAt).toLocaleString(APP_LOCALE)}
                {s.completedAt ? ` · answered ${new Date(s.completedAt).toLocaleString(APP_LOCALE)}` : ''}
                {s.reusedProfile ? ' · existing provider profile reused' : ''}
              </Typography>
              {s.detail ? <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>{s.detail}</Typography> : null}
              {canAct && s.role === 'PAYER' && s.entityType === 'COMPANY' ? (
                <Button size="small" sx={{ mt: 0.5, ml: -0.75 }} onClick={() => setUboFor(s)}>Screen a UBO of this company</Button>
              ) : null}
            </Box>
          ))}
        </Stack>
      ) : null}

      {editing ? <EditSenderDialog payment={payment} onClose={() => setEditing(false)} /> : null}
      {uboFor !== null ? <UboDialog paymentId={payment.id} company={uboFor} onClose={() => setUboFor(null)} onScreened={refresh} /> : null}
    </Box>
  );
}

function Field({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <Box>
      <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{value}</Typography>
    </Box>
  );
}

function EditSenderDialog({ payment, onClose }: { readonly payment: InboundPaymentDetail; readonly onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PayerEdit>({
    reason: '',
    payerType: payment.payerType ?? null,
    payerCountry: payment.payerCountry ?? '',
    payerAddress: payment.payerAddress ?? null,
    payerRegistrationNumber: payment.payerRegistrationNumber ?? null,
    senderBankName: payment.senderBankName,
    senderBankBic: payment.senderBankBic,
    senderBankCountry: payment.senderBankCountry,
  });
  const [error, setError] = useState<string | null>(null);
  const set = (key: keyof PayerEdit, value: string): void =>
    setForm((f) => ({ ...f, [key]: key === 'reason' || key === 'payerCountry' ? value : value === '' ? null : value }));

  const save = useMutation({
    mutationFn: () => inboundPaymentsApi.editPayer(payment.id, {
      ...form,
      payerCountry: form.payerCountry.trim().toUpperCase(),
      payerRegistrationNumber: form.payerType === 'COMPANY' ? form.payerRegistrationNumber : null,
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbound-payment-detail', payment.id] });
      void queryClient.invalidateQueries({ queryKey: ['inbound-payments'] });
      onClose();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'The sender details could not be saved.'),
  });

  const countryOk = /^[A-Za-z]{2}$/.test(form.payerCountry.trim());
  const reasonOk = form.reason.trim().length >= 10;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="edit-sender-title">
      <DialogTitle id="edit-sender-title">Edit sender details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error !== null ? <Alert severity="error">{error}</Alert> : null}
          <Alert severity="info">
            The transaction screening was run on the details as they are now. After a change, releasing this payment
            needs a fresh screening clearance. The partner&rsquo;s original details are kept.
          </Alert>
          <TextField select size="small" label="The sender is" value={form.payerType ?? ''} onChange={(e) => set('payerType', e.target.value)}>
            <MenuItem value="">Not established</MenuItem>
            <MenuItem value="PERSON">A person</MenuItem>
            <MenuItem value="COMPANY">A company</MenuItem>
          </TextField>
          <TextField
            size="small" required label="Sender country (ISO, e.g. CY)" value={form.payerCountry}
            onChange={(e) => set('payerCountry', e.target.value)} inputProps={{ maxLength: 2 }}
            error={form.payerCountry !== '' && !countryOk}
          />
          {form.payerType === 'COMPANY' ? (
            <TextField
              size="small" label="Registration number (optional)" value={form.payerRegistrationNumber ?? ''}
              onChange={(e) => set('payerRegistrationNumber', e.target.value)} inputProps={{ maxLength: 60 }}
              helperText="Identifies the company for screening. Without it, name and country are used."
            />
          ) : null}
          <TextField size="small" label="Sender address (optional)" value={form.payerAddress ?? ''} onChange={(e) => set('payerAddress', e.target.value)} inputProps={{ maxLength: 300 }} />
          <TextField size="small" label="Sender bank (optional)" value={form.senderBankName ?? ''} onChange={(e) => set('senderBankName', e.target.value)} inputProps={{ maxLength: 200 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" fullWidth label="Bank BIC (optional)" value={form.senderBankBic ?? ''} onChange={(e) => set('senderBankBic', e.target.value)} inputProps={{ maxLength: 11 }} />
            <TextField size="small" fullWidth label="Bank country (optional)" value={form.senderBankCountry ?? ''} onChange={(e) => set('senderBankCountry', e.target.value)} inputProps={{ maxLength: 2 }} />
          </Stack>
          <TextField
            size="small" required multiline minRows={2} label="Why are the details changing?" value={form.reason}
            onChange={(e) => set('reason', e.target.value)} inputProps={{ maxLength: 2000 }}
            helperText="At least 10 characters. Kept on the client's file with your name."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button variant="contained" disabled={!countryOk || !reasonOk || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UboDialog({
  paymentId,
  company,
  onClose,
  onScreened,
}: {
  readonly paymentId: string;
  readonly company: CounterpartyScreening;
  readonly onClose: () => void;
  readonly onScreened: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const screen = useMutation({
    mutationFn: () => inboundPaymentsApi.screenCounterparty(paymentId, {
      role: 'UBO',
      entityType: 'PERSON',
      name: name.trim(),
      country: country.trim().toUpperCase(),
      parentScreeningId: company.id,
      ...(dateOfBirth !== '' ? { dateOfBirth } : {}),
      ...(address.trim() !== '' ? { address: address.trim() } : {}),
    }),
    onSuccess: () => { onScreened(); onClose(); },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'The UBO could not be screened.'),
  });
  const ok = name.trim().length >= 2 && /^[A-Za-z]{2}$/.test(country.trim());

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="ubo-title">
      <DialogTitle id="ubo-title">Screen a UBO of {company.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error !== null ? <Alert severity="error">{error}</Alert> : null}
          <TextField size="small" required label="Full name" value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 200 }} />
          <TextField size="small" required label="Citizenship (ISO, e.g. HR)" value={country} onChange={(e) => setCountry(e.target.value)} inputProps={{ maxLength: 2 }} />
          <TextField size="small" type="date" label="Date of birth (optional)" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Address (optional)" value={address} onChange={(e) => setAddress(e.target.value)} inputProps={{ maxLength: 300 }} />
          <Typography variant="caption" color="text.secondary">
            The person is linked to the company at the provider as its beneficial owner and screened. Screening the
            same person again reuses their profile.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={screen.isPending}>Cancel</Button>
        <Button variant="contained" disabled={!ok || screen.isPending} onClick={() => screen.mutate()}>
          {screen.isPending ? 'Starting…' : 'Screen UBO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
